import React, {useEffect, useMemo, useState} from 'react';
import {useIntl} from 'react-intl';
import {Container} from 'react-bootstrap';
import {Link, useNavigate} from 'react-router-dom';
import {DateTime} from 'luxon';
import ReactSelect, {components, OptionProps} from 'react-select';
import {ColumnDef, PaginationState} from '@tanstack/react-table';
import {PageTitle} from '../../../_metronic/layout/core';
import TableMetronic from '../../../app/common/components/table/TableMetronic';
import {DivopsAccountFilterOption, DivopsHistoryQueryParams, DivopsSearchHistoryEntry} from '../Types/types';
import {useGetSearchHistory, useGetSearchHistoryFilters} from '../Hooks/DivopsHooks';

interface FilterOption {
  value: string;
  label: string;
}

const filterSelectStyles = {
  menuPortal: (base: Record<string, unknown>) => ({...base, zIndex: 9999}),
};

const filterDateInputStyle = {height: 38, fontSize: '0.96rem', minWidth: 140};

const filterDateAddonStyle = {height: 38, fontSize: '0.85rem'};

const CheckboxOption = (props: OptionProps<FilterOption, true>) => (
  <components.Option {...props}>
    <div className="d-flex align-items-start">
      <input type="checkbox" checked={props.isSelected} onChange={() => null} className="me-2 mt-1 flex-shrink-0" />
      <label className="mb-0 flex-grow-1" style={{wordBreak: 'break-word'}}>
        {props.label}
      </label>
    </div>
  </components.Option>
);

const toOptions = (values: string[] = []): FilterOption[] => values.map((value) => ({value, label: value}));

const toAccountOptions = (accounts: DivopsAccountFilterOption[] = []): FilterOption[] =>
  accounts.map(({id, name}) => ({value: id, label: name ? `${id} - ${name}` : id}));

const DivopsHistoryPage = (): JSX.Element => {
  const intl = useIntl();
  const navigate = useNavigate();

  const [selectedAccounts, setSelectedAccounts] = useState<FilterOption[]>([]);
  const [selectedUsers, setSelectedUsers] = useState<FilterOption[]>([]);
  const [selectedCommunes, setSelectedCommunes] = useState<FilterOption[]>([]);
  const [exactDate, setExactDate] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [paginationState, setPaginationState] = useState<PaginationState>({pageIndex: 0, pageSize: 10});

  useEffect(() => {
    setPaginationState((prev) => (prev.pageIndex === 0 ? prev : {...prev, pageIndex: 0}));
  }, [selectedAccounts, selectedUsers, selectedCommunes, exactDate, dateFrom, dateTo]);

  const {data: filters} = useGetSearchHistoryFilters();
  const accountOptions = useMemo(() => toAccountOptions(filters?.accounts), [filters]);
  const userOptions = useMemo(() => toOptions(filters?.users), [filters]);
  const communeOptions = useMemo(() => toOptions(filters?.communes), [filters]);

  const queryParams: DivopsHistoryQueryParams = useMemo(
    () => ({
      page: paginationState.pageIndex + 1,
      perPage: paginationState.pageSize,
      accountIds: selectedAccounts.length ? selectedAccounts.map((o) => o.value) : undefined,
      userEmails: selectedUsers.length ? selectedUsers.map((o) => o.value) : undefined,
      communes: selectedCommunes.length ? selectedCommunes.map((o) => o.value) : undefined,
      dateExact: exactDate || undefined,
      dateFrom: dateFrom || undefined,
      dateTo: dateTo || undefined,
    }),
    [paginationState, selectedAccounts, selectedUsers, selectedCommunes, exactDate, dateFrom, dateTo]
  );

  const {data: response, isLoading, isFetching} = useGetSearchHistory(queryParams);

  const columns = useMemo<ColumnDef<DivopsSearchHistoryEntry>[]>(
    () => [
      {
        header: intl.formatMessage({id: 'DIVOPS.HISTORY.COL.ADRESSE'}),
        accessorKey: 'adresse',
        enableSorting: false,
      },
      {
        header: intl.formatMessage({id: 'DIVOPS.HISTORY.COL.CODE_POSTAL'}),
        accessorKey: 'codePostal',
        size: 100,
        enableSorting: false,
      },
      {
        header: intl.formatMessage({id: 'DIVOPS.HISTORY.COL.COMMUNE'}),
        accessorKey: 'commune',
        enableSorting: false,
      },
      {
        header: intl.formatMessage({id: 'DIVOPS.HISTORY.COL.DATE'}),
        accessorKey: 'createdAt',
        cell: (info) => DateTime.fromISO(info.getValue<string>()).toFormat('dd/MM/yyyy HH:mm'),
        size: 150,
        enableSorting: false,
      },
      {
        header: intl.formatMessage({id: 'DIVOPS.HISTORY.COL.EMAIL'}),
        accessorKey: 'userEmail',
        enableSorting: false,
      },
      {
        header: intl.formatMessage({id: 'DIVOPS.HISTORY.COL.ACCOUNT_ID'}),
        accessorKey: 'accountId',
        size: 100,
        enableSorting: false,
      },
      {
        header: '',
        id: 'actions',
        size: 60,
        enableSorting: false,
        cell: (info) => (
          <button
            type="button"
            className="btn btn-sm btn-icon btn-light-primary"
            title={intl.formatMessage({id: 'DIVOPS.HISTORY.RELAUNCH'})}
            disabled={!info.row.original.codeInsee}
            onClick={() =>
              navigate('/divops', {
                state: {
                  replaySearch: {
                    adresse: info.row.original.adresse,
                    codePostal: info.row.original.codePostal,
                    codeInsee: info.row.original.codeInsee,
                    label: `${info.row.original.adresse} ${info.row.original.codePostal} ${info.row.original.commune}`,
                  },
                },
              })
            }
          >
            <i className="fas fa-search"></i>
          </button>
        ),
      },
    ],
    [intl, navigate]
  );

  const filtersToolbar = (
    <>
      <div style={{minWidth: 220, flex: 1, maxWidth: 320}}>
        <ReactSelect<FilterOption, true>
          isMulti
          closeMenuOnSelect={false}
          hideSelectedOptions={false}
          components={{Option: CheckboxOption}}
          options={accountOptions}
          value={selectedAccounts}
          onChange={(vals) => setSelectedAccounts(vals ? [...vals] : [])}
          placeholder={intl.formatMessage({id: 'DIVOPS.HISTORY.FILTER.ALL_ACCOUNTS'})}
          noOptionsMessage={() => intl.formatMessage({id: 'FIELD.NO.OPTIONS'})}
          classNamePrefix="filter-select"
          menuPortalTarget={document.body}
          styles={filterSelectStyles}
        />
      </div>
      <div style={{minWidth: 220, flex: 1, maxWidth: 320}}>
        <ReactSelect<FilterOption, true>
          isMulti
          closeMenuOnSelect={false}
          hideSelectedOptions={false}
          components={{Option: CheckboxOption}}
          options={userOptions}
          value={selectedUsers}
          onChange={(vals) => setSelectedUsers(vals ? [...vals] : [])}
          placeholder={intl.formatMessage({id: 'DIVOPS.HISTORY.FILTER.ALL_USERS'})}
          noOptionsMessage={() => intl.formatMessage({id: 'FIELD.NO.OPTIONS'})}
          classNamePrefix="filter-select"
          menuPortalTarget={document.body}
          styles={filterSelectStyles}
        />
      </div>
      <div style={{minWidth: 220, flex: 1, maxWidth: 320}}>
        <ReactSelect<FilterOption, true>
          isMulti
          closeMenuOnSelect={false}
          hideSelectedOptions={false}
          components={{Option: CheckboxOption}}
          options={communeOptions}
          value={selectedCommunes}
          onChange={(vals) => setSelectedCommunes(vals ? [...vals] : [])}
          placeholder={intl.formatMessage({id: 'DIVOPS.HISTORY.FILTER.ALL_COMMUNES'})}
          noOptionsMessage={() => intl.formatMessage({id: 'FIELD.NO.OPTIONS'})}
          classNamePrefix="filter-select"
          menuPortalTarget={document.body}
          styles={filterSelectStyles}
        />
      </div>
      <div className="input-group" style={{width: 'auto'}}>
        <span className="input-group-text" style={filterDateAddonStyle}>
          {intl.formatMessage({id: 'DIVOPS.HISTORY.FILTER.DATE_EXACT'})}
        </span>
        <input
          type="date"
          className="form-control"
          style={filterDateInputStyle}
          value={exactDate}
          onChange={(e) => setExactDate(e.target.value)}
        />
      </div>
      <div className="input-group" style={{width: 'auto'}}>
        <span className="input-group-text" style={filterDateAddonStyle}>
          {intl.formatMessage({id: 'DIVOPS.HISTORY.FILTER.DATE_FROM'})}
        </span>
        <input
          type="date"
          className="form-control"
          style={filterDateInputStyle}
          value={dateFrom}
          onChange={(e) => setDateFrom(e.target.value)}
        />
      </div>
      <div className="input-group" style={{width: 'auto'}}>
        <span className="input-group-text" style={filterDateAddonStyle}>
          {intl.formatMessage({id: 'DIVOPS.HISTORY.FILTER.DATE_TO'})}
        </span>
        <input
          type="date"
          className="form-control"
          style={filterDateInputStyle}
          value={dateTo}
          onChange={(e) => setDateTo(e.target.value)}
        />
      </div>
    </>
  );

  return (
    <Container fluid className="px-0">
      <PageTitle
        breadcrumbs={[
          {title: 'DivOps', path: '/divops', isSeparator: false, isActive: false},
        ]}
      >
        {intl.formatMessage({id: 'DIVOPS.HISTORY.PAGE_TITLE'})}
      </PageTitle>

      <div className="card mx-5">
        <div className="card-body py-10">
          <div className="d-flex justify-content-end mb-6">
            <Link to="/divops" className="btn btn-light-primary">
              <i className="fas fa-arrow-left me-2"></i>
              {intl.formatMessage({id: 'DIVOPS.HISTORY.BACK'})}
            </Link>
          </div>

          <TableMetronic<DivopsSearchHistoryEntry>
            toolbar={filtersToolbar}
            stackedHeader
            searchBar={false}
            data={response?.data ?? []}
            columns={columns}
            isLoading={isLoading || isFetching}
            pagination={true}
            manualPagination
            paginationState={paginationState}
            onPaginationChange={setPaginationState}
            pageCount={response?.meta.totalPages ?? 0}
            rowCount={response?.meta.total ?? 0}
            noRowsMessage={intl.formatMessage({id: 'DIVOPS.HISTORY.EMPTY'})}
          />
        </div>
      </div>
    </Container>
  );
};

export default DivopsHistoryPage;
